import { normalizeForSearch } from 'shared/utils/search';
import { type FilterCondition, type FilterGroup, type FilterNode, groupOf, isFilterGroup } from './filterTree';
import {
  SingleFilter,
  Filter,
  AsyncFilter,
  RelationsMap,
  FILTER_TYPE,
  FILTER_LOGICAL_OPERATOR,
  type FilterSkip,
} from './types';

const SEARCH_KEY_BLACKLIST = [
  '_id',
  'documents',
  'events',
  'createdAt',
  'modifiedAt',
  'assignee',
  'createdBy',
  'modifiedBy',
  'executor',
  'documentationId',
  'versionId',
  'project',
  'site',
  'maps',
  'positionOnMap',
  'azureId',
  'inspection',
  'protocolItem',
  'userAccesses',
  'detectionDate',
  'contractNumbers',
  'supervisor',
  'employmentType',
  'injuryTypes',
  'injuredBodyParts',
  'requirements',
  'installationBasis',
  'constructionProductDocuments',
  'deviceDocuments',
  'additionalDocuments',
] as string[];

// https://gist.github.com/harish2704/d0ee530e6ee75bad6fd30c98e5ad9dab
export const getPropByPath = <T>(
  object: T | object | any[] | undefined,
  path: string | string[]
): any => {
  if (path === '') {
    return object;
  }
  const _path = Array.isArray(path) ? path : path.split('.');
  if (object && _path.length)
    //@ts-ignore
    return getPropByPath(object[_path.shift()], _path);
  return object;
};

function normalizeBooleanLike(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }

  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }

  return null;
}

function areFilterIdentifiersEqual(left: unknown, right: unknown): boolean {
  const normalizedLeft = normalizeBooleanLike(left);
  const normalizedRight = normalizeBooleanLike(right);

  if (normalizedLeft !== null && normalizedRight !== null) {
    return normalizedLeft === normalizedRight;
  }

  return String(left) === String(right);
}

function toSearchableFilterValue(value: unknown): string {
  if (value == null) {
    return '';
  }

  if (Array.isArray(value)) {
    return value.map(toSearchableFilterValue).join(' ');
  }

  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map(toSearchableFilterValue)
      .join(' ');
  }

  return String(value);
}

// Multiple types of filtering values
export const filtersByType: Record<string, (
    value: any | undefined,
    filter: SingleFilter['value']
  ) => boolean> = {
  // if value is array of {_id} we need to match at least one
  oneOfIds: (value: { _id: string }[] | undefined, filterValues) => {
    if (!Array.isArray(filterValues)) {
      return true;
    }
    if (!value) {
      return false;
    }
    return value.find((elem) => {
      return filterValues.find((filterValue) => {
        return areFilterIdentifiersEqual(filterValue._id, elem._id);
      });
    })
      ? true
      : false;
  },
  oneOfDirect: (value: string[] | undefined, filterValues: unknown) => {
    if (!Array.isArray(filterValues)) {
      return true;
    }
    if (!value) {
      return false;
    }

    return value.find((elem: string) => {
      return filterValues.find((filterValue) => {
        return areFilterIdentifiersEqual(filterValue._id, elem);
      });
    })
      ? true
      : false;
  },
  // if value is just {_id}
  directId: (value: { _id: string } | undefined, filterValues) => {
    if (!Array.isArray(filterValues)) {
      return true;
    }
    if (!value) {
      return false;
    }
    return filterValues.find((filterValue) => {
      return areFilterIdentifiersEqual(filterValue._id, value._id);
    })
      ? true
      : false;
  },
  // if value is string
  direct: (value, filterValues) => {
    if (!Array.isArray(filterValues)) {
      return true;
    }
    return filterValues.find((filterValue) => {
      return areFilterIdentifiersEqual(filterValue._id, value);
    })
      ? true
      : false;
  },
  // if we want to find element between two dates
  dateRange: (value, filterValues) => {
    if (
      Array.isArray(filterValues) ||
      typeof filterValues === 'string' ||
      !filterValues ||
      typeof filterValues !== 'object'
    ) {
      return false;
    }
    if (!value) return false;
    // comparing unix timestamp should do the job perfectly
    // curr is current value, not current timestamp
    const curr = new Date(value).getTime();
    if (!Number.isFinite(curr)) {
      return false;
    }
    // if to or from value is undefined we set it to element timestamp
    // that results in match (i.e if both are undefined - (curr >= curr && curr <= curr) === true)
    const from = filterValues.fromDate
      ? new Date(filterValues.fromDate).getTime()
      : curr;
    const to = filterValues.toDate
      ? new Date(filterValues.toDate).getTime()
      : curr;

    if (!Number.isFinite(from) || !Number.isFinite(to)) {
      return false;
    }

    return curr >= from && curr <= to;
  },
  contains: (value, filterValue) => {
    if (typeof filterValue !== 'string') {
      return true;
    }

    return normalizeForSearch(toSearchableFilterValue(value)).includes(
      normalizeForSearch(filterValue)
    );
  },
  doesNotContain: (value, filterValue) => {
    if (typeof filterValue !== 'string') {
      return true;
    }

    return !normalizeForSearch(toSearchableFilterValue(value)).includes(
      normalizeForSearch(filterValue)
    );
  },
  equals: (value, filterValue) => {
    if (typeof filterValue !== 'string') {
      return true;
    }

    return (
      normalizeForSearch(toSearchableFilterValue(value)) ===
      normalizeForSearch(filterValue)
    );
  },
  doesNotEqual: (value, filterValue) => {
    if (typeof filterValue !== 'string') {
      return true;
    }

    return (
      normalizeForSearch(toSearchableFilterValue(value)) !==
      normalizeForSearch(filterValue)
    );
  },
  startsWith: (value, filterValue) => {
    if (typeof filterValue !== 'string') {
      return true;
    }

    return normalizeForSearch(toSearchableFilterValue(value)).startsWith(
      normalizeForSearch(filterValue)
    );
  },
  endsWith: (value, filterValue) => {
    if (typeof filterValue !== 'string') {
      return true;
    }

    return normalizeForSearch(toSearchableFilterValue(value)).endsWith(
      normalizeForSearch(filterValue)
    );
  },
  isEmpty: (value) => {
    return normalizeForSearch(toSearchableFilterValue(value)).length === 0;
  },
  isNotEmpty: (value) => {
    return normalizeForSearch(toSearchableFilterValue(value)).length > 0;
  },
  isAnyOf: (value, filterValues) => {
    if (!Array.isArray(filterValues)) {
      return true;
    }

    const normalizedValue = normalizeForSearch(
      toSearchableFilterValue(value)
    );
    const stringValues = filterValues.filter(
      (filterValue) => typeof filterValue === 'string'
    ) as string[];

    return stringValues.some(
      (filterValue) => normalizeForSearch(filterValue) === normalizedValue
    );
  },
  // search in value for user search key, value may be: string, number, array, object, boolean
  search: (value, filterValues) => {
    if (typeof filterValues !== 'string') {
      return false;
    }
    const findInObj = (obj: any, val: string): any[] => {
      const objects = [];
      val = normalizeHashtagSearch(val);
      for (const i in obj) {
        if (!obj.hasOwnProperty(i) || SEARCH_KEY_BLACKLIST.includes(i))
          continue;
        // recursively go deeper
        if (typeof obj[i] == 'object') {
          const subRes = findInObj(obj[i], val);
          if (subRes) {
            // react 18 types
            // @ts-ignore
            objects.push(...subRes);
          }
        } else {
          // if value is an array we need to...
          if (Array.isArray(obj[i])) {
            // ...iterate...
            for (const a in obj[i]) {
              // ...and recursively go deeper
              const subRes = findInObj(obj[i][a], val);
              if (subRes) {
                // react 18 types
                // @ts-ignore
                objects.push(...subRes);
              }
            }
          } else {
            //skip _id fields - this might change to an array of blacklisted keys

            // turn value to lower case, enforce string,
            // do the same for search key and then search in string
            const match = normalizeForSearch(obj[i]).includes(
              normalizeForSearch(val)
            );
            if (match) {
              // react 18 types
              // @ts-ignore
              objects.push(i);
            }
          }
        }
      }
      return objects;
    };

    return findInObj(value, filterValues).length > 0;
  },
};

/** Whether the element's value at the condition's path matches the filter, before a negation. */
function matchesFilterValue<T>(element: T, filter: SingleFilter): boolean {
  const matches = filtersByType[filter.type];
  return matches ? matches(getPropByPath(element, filter.path), filter.value) : false;
}

/**
 * Whether the element meets one condition. An invalid filter never matches.
 * The `skip` rule of the caller decides first (PT-3985: a field not visible
 * on the issue's process): `true` passes the element, `'exclude'` leaves it
 * out, unless the condition is negated: a hidden field has no value, so it
 * "is not" any value.
 */
export function matchesCondition<T>(element: T, condition: FilterCondition, skip?: FilterSkip<T>): boolean {
  if (condition.type === FILTER_TYPE.invalid) {
    return false;
  }
  const skipVerdict = skip ? skip(element, condition) : false;
  if (skipVerdict === true) {
    return true;
  }
  if (skipVerdict === 'exclude') {
    return Boolean(condition.negated);
  }
  const matches = matchesFilterValue(element, condition);
  return condition.negated ? !matches : matches;
}

/** Whether the element meets the group: all its nodes (AND) or any of them (OR); an empty group matches. */
export function matchesFilterGroup<T>(element: T, group: FilterGroup, skip?: FilterSkip<T>): boolean {
  if (!group.nodes.length) {
    return true;
  }
  const matchesNode = (node: FilterNode): boolean =>
    isFilterGroup(node) ? matchesFilterGroup(element, node, skip) : matchesCondition(element, node, skip);
  return group.operator === FILTER_LOGICAL_OPERATOR.OR ? group.nodes.some(matchesNode) : group.nodes.every(matchesNode);
}

/** The elements meeting the group of conditions. */
export function filterByGroup<T>({ set, group, skip }: { set: T[]; group: FilterGroup; skip?: FilterSkip<T> }): T[] {
  return set.filter((element) => matchesFilterGroup(element, group, skip));
}

/** Runs flat filters on the set: all of them, or any of them with `options.any`. */
export const filtering: Filter = ({ set, filters, options }) =>
  filterByGroup({
    set,
    group: groupOf(options?.any ? FILTER_LOGICAL_OPERATOR.OR : FILTER_LOGICAL_OPERATOR.AND, filters),
    skip: options?.skip,
  });

// Generates array of filters out of searchKey and relations hash map
const genRelationSearchFilters = (
  relationsMap: RelationsMap,
  searchKey?: string
): SingleFilter[] | [] => {
  if (!searchKey) {
    return [];
  }
  const res: SingleFilter[] = [];
  Object.keys(relationsMap.hashMap).forEach((key) => {
    const elems = Object.entries(relationsMap.hashMap[key]);
    // get elements which "name" matches search string
    // and map them to array of '_id' objects as regular filter obj value key
    const matchingIds = elems
      .filter(([id, name]) => {
        return normalizeForSearch(name).includes(
          normalizeForSearch(searchKey)
        );
      })
      .map(([id]) => ({ _id: id }));
    // get matches for relation,
    // matches represent filter obj without value
    // and populate it with value from above - resulting in filter array
    relationsMap.matches
      .find((elem: any) => elem.name === key)
      ?.matches.forEach((match: any) => {
        res.push({ ...match, value: matchingIds });
      });
  });

  return res;
};

// runs filters created by generated search filters map
// out of relation hash map and search key itself
export const searching: AsyncFilter = async ({
  set,
  filters,
  relationsMap,
}) => {
  const searchKey = filters.find((elem) => elem.type === FILTER_TYPE.search)
    ?.value;

  const relationFilters = relationsMap
    ? await genRelationSearchFilters(relationsMap, searchKey as string)
    : [];
  if (filters.length + relationFilters.length === 0) {
    return set;
  }
  return filtering({
    set,
    filters: [...filters, ...relationFilters],
    options: { any: true },
  });
};

function normalizeHashtagSearch(value: string): string {
  if (value.startsWith('#') && value.length >= 4 && value.length <= 5) {
    return value.slice(1);
  }
  return value;
}
